import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (this.#handleCartUpdate),
    );
    document.addEventListener(
      ThemeEvents.discountUpdate,
      /** @type {EventListener} */ (this.handleDiscountUpdate),
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (this.#debouncedOnChange),
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      /** @type {EventListener} */ (this.#handleCartUpdate),
    );
    document.removeEventListener(
      ThemeEvents.discountUpdate,
      /** @type {EventListener} */ (this.handleDiscountUpdate),
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      /** @type {EventListener} */ (this.#debouncedOnChange),
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) {
      this.updateQuantity({
        line,
        quantity: 0,
        action: "clear",
      });
      return;
    }

    const bundleId =
      cartItemRowToRemove.dataset.bundleId ||
      cartItemRowToRemove.dataset.bundleKey;
    const parentId = cartItemRowToRemove.dataset.parentId;
    const parentKey =
      cartItemRowToRemove.dataset.parentKey ||
      cartItemRowToRemove.dataset.parentKeyProp;
    const itemKey = cartItemRowToRemove.dataset.key;
    const regalo1Handle = cartItemRowToRemove.dataset.regalo1Handle;
    const regalo2Handle = cartItemRowToRemove.dataset.regalo2Handle;
    const regalo3Handle = cartItemRowToRemove.dataset.regalo3Handle;
    const regalo1Id = cartItemRowToRemove.dataset.regalo1Id;
    const regalo2Id = cartItemRowToRemove.dataset.regalo2Id;
    const regalo3Id = cartItemRowToRemove.dataset.regalo3Id;

    const giftTargets = [
      regalo1Handle,
      regalo2Handle,
      regalo3Handle,
      regalo1Id,
      regalo2Id,
      regalo3Id,
    ].filter(Boolean);

    const rowsToRemoveSet = new Set([cartItemRowToRemove]);

    this.refs.cartItemRows.forEach((row) => {
      if (row === cartItemRowToRemove) return;

      const rowBundleId = row.dataset.bundleId || row.dataset.bundleKey;
      const rowParentId = row.dataset.parentId;
      const rowParentKey = row.dataset.parentKey || row.dataset.parentKeyProp;

      if (bundleId && rowBundleId === bundleId) {
        rowsToRemoveSet.add(row);
        return;
      }
      if (parentKey && row.dataset.parentKey === parentKey) {
        rowsToRemoveSet.add(row);
        return;
      }
      if (itemKey && (rowParentId === itemKey || rowParentKey === itemKey)) {
        rowsToRemoveSet.add(row);
        return;
      }
      if (giftTargets.length) {
        if (
          giftTargets.includes(row.dataset.handle) ||
          giftTargets.includes(row.dataset.productId)
        ) {
          rowsToRemoveSet.add(row);
          return;
        }
      }
    });

    // If removing this item leaves only gifts in the cart, remove the orphan gifts as well
    const remainingNonGiftRows = this.refs.cartItemRows.filter(
      (r) => !rowsToRemoveSet.has(r) && r.dataset.isGift !== "true"
    );
    if (remainingNonGiftRows.length === 0) {
      this.refs.cartItemRows.forEach((r) => rowsToRemoveSet.add(r));
    }

    const rowsToRemove = Array.from(rowsToRemoveSet);

    if (rowsToRemove.length > 1) {
      const updates = {};
      rowsToRemove.forEach((row) => {
        if (row.dataset.key) {
          updates[row.dataset.key] = 0;
        }
      });

      this.updateQuantity({
        line,
        quantity: 0,
        updates,
        action: "clear",
      });
    } else {
      this.updateQuantity({
        line,
        quantity: 0,
        action: "clear",
      });
    }

    // If the cart item rows are the last rows, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length === this.refs.cartItemRows.length;

    const template = document.getElementById("empty-cart-template");
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? "empty-cart-drawer" : "empty-cart-page"]);

      return;
    }

    // Add class to the rows to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {Record<string, number>} [config.updates] - Optional map of item keys to quantities for bulk updates.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    this.#disableCartItems();

    const { line, quantity, updates } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const isUpdateEndpoint = Boolean(updates);
    const cartEndpoint = isUpdateEndpoint
      ? Theme.routes.cart_update_url
      : Theme.routes.cart_change_url;

    const bodyPayload = updates
      ? {
          updates,
          sections: Array.from(sectionsToUpdate).join(","),
          sections_url: window.location.pathname,
        }
      : {
          line: line,
          quantity: quantity,
          sections: Array.from(sectionsToUpdate).join(","),
          sections_url: window.location.pathname,
        };

    const body = JSON.stringify(bodyPayload);

    cartTotal?.shimmer();

    fetch(`${cartEndpoint}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html",
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]',
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          }),
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId],
          { mode: this.isDrawer ? "hydration" : "full" },
        );

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"]`,
      );

      for (const selector of selectors) {
        const input = selector.querySelector("input[data-cart-quantity]");
        if (!input) continue;

        input.setAttribute("data-cart-quantity", item.quantity.toString());

        // Update the quantity selector's internal state
        if (
          "updateCartQuantity" in selector &&
          typeof selector.updateCartQuantity === "function"
        ) {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
